import { useFormikContext } from 'formik';
import { pick } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { Form, ThemeVisualizer, Wrapper } from 'components';
import { MediaUpload } from 'components/Form/inputs/MediaUpload/MediaUpload';
import { useMediaQuery } from 'react-responsive';
import { uploadMedia } from 'utils/mediaResourceUtils';
import { breakpoints } from 'utils/rwd';
import { ONBOARDING } from '../../constants/routes';
import { useUpdateTheme } from '../../api/brand/hooks';
import { mapInitialValues } from '../../utils/themeCustomizationFormUtils';

import { ReactComponent as Icon } from '../../assets/img/illustrations/theme-customization.svg';

const ThemeCustomizationForm = ({ buttonText, handleClick, initialValues, onNext }) => {
  const { t } = useTranslation(['pages']);
  const location = useLocation();
  const { mutate: updateTheme, isPending } = useUpdateTheme();
  const mappedInitialValues = useMemo(() => mapInitialValues(initialValues), [initialValues]);
  const [themeColor, setThemeColor] = useState(initialValues?.colour ?? '#000000');
  const [file, setFile] = useState(null);

  const isMediumScreen = useMediaQuery({ minWidth: breakpoints.md });
  const isLargeScreen = useMediaQuery({ minWidth: breakpoints.lg });

  const FormObserver = () => {
    const { values } = useFormikContext();

    useEffect(() => {
      setThemeColor(values.color);
    }, [values.color]);

    return null;
  };
  return (
    <Form
      initialValues={
        pick(mappedInitialValues, ['uid', 'thumbnail', 'images', 'color']) || {
          thumbnail: [],
          images: [],
          color: '#000000',
        }
      }
      onSubmit={async ({ uid, thumbnail, color }) => {
        const { public_id } = await uploadMedia(thumbnail);
        updateTheme(
          {
            uid,
            color,
            picture_id: public_id,
            is_onboarding: location.pathname.includes(ONBOARDING),
          },
          {
            onSuccess: () => {
              if (onNext) onNext();
            },
          },
        );
      }}
      validationSchema={Yup.object({
        color: Yup.string().required(t('validation:required')),
        thumbnail: Yup.array().min(1, t('validation:required')).required(t('validation:required')),
      })}
      renderDefaultButtons={false}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          marginTop: isMediumScreen ? '1rem' : '0.5rem',
          marginBottom: isMediumScreen ? '2rem' : '1rem',
        }}
      >
        <div className="d-flex justify-content-center">
          <div
            style={{ width: isLargeScreen ? '40%' : isMediumScreen ? '60%' : '80%' }}
            className="py-4"
          >
            {themeColor || file ? <ThemeVisualizer mainColor={themeColor} /> : <Icon />}
          </div>
        </div>
      </div>
      <Form.Group>
        <Form.Label>{t('brandRegistration.color')}</Form.Label>
        <Form.Input
          className="p-3"
          name="color"
          type="color"
          style={{ height: '42px', width: '100%' }}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label>{t(`brandRegistration.logo`)}</Form.Label>
        <MediaUpload
          accept={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
          }}
          name="thumbnail"
          maxFiles={1}
          setFile={setFile}
          withCrop
        />
      </Form.Group>
      <Wrapper style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Form.Submit
          className="mt-3"
          size="lg"
          variant="primary"
          onClick={handleClick}
          pristine
          isLoading={isPending}
        >
          {buttonText || `${t('brandRegistration.save')}`}
        </Form.Submit>
      </Wrapper>
      <FormObserver />
    </Form>
  );
};

ThemeCustomizationForm.propTypes = {
  handleClick: PropTypes.func,
  buttonText: PropTypes.string,
  initialValues: PropTypes.object,
  onNext: PropTypes.func,
};

export default ThemeCustomizationForm;
